/**
 * Thin fetch wrapper for the /api/* surface.
 *
 * - Sends/receives JSON.
 * - Includes credentials (cookies) by default.
 * - Mirrors the `ct_csrf` cookie into the `X-CSRF-Token` header on state-
 *   changing requests so the backend's double-submit check passes.
 */

const CSRF_COOKIE = 'ct_csrf';

function readCsrfFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name === CSRF_COOKIE) {
      return decodeURIComponent(rest.join('=') ?? '');
    }
  }
  return null;
}

export interface ApiError {
  status: number;
  error: string;
  issues?: { path: (string | number)[]; message: string; code?: string }[];
}

export class ApiResponseError extends Error {
  readonly status: number;
  readonly error: string;
  readonly issues?: ApiError['issues'];

  constructor(detail: ApiError) {
    super(detail.error);
    this.status = detail.status;
    this.error = detail.error;
    this.issues = detail.issues;
  }
}

type Body = Record<string, unknown> | undefined;

const STATE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

async function request<T>(
  method: string,
  path: string,
  body?: Body,
  init?: RequestInit,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };
  if (body !== undefined && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  if (STATE_METHODS.has(method.toUpperCase())) {
    const csrf = readCsrfFromCookie();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    ...init,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: ApiError = { status: res.status, error: 'request_failed' };
    try {
      const json = (await res.json()) as Partial<ApiError>;
      detail = {
        status: res.status,
        error: typeof json.error === 'string' ? json.error : 'request_failed',
        issues: json.issues,
      };
    } catch {
      // ignore — keep default
    }
    throw new ApiResponseError(detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T,>(path: string, init?: RequestInit) => request<T>('GET', path, undefined, init),
  post: <T,>(path: string, body?: Body, init?: RequestInit) =>
    request<T>('POST', path, body, init),
  patch: <T,>(path: string, body?: Body, init?: RequestInit) =>
    request<T>('PATCH', path, body, init),
  delete: <T,>(path: string, init?: RequestInit) => request<T>('DELETE', path, undefined, init),
  /** Upload a Blob/File body without JSON-encoding. */
  async upload<T>(path: string, body: Blob | ArrayBuffer, contentType: string): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': contentType,
    };
    const csrf = readCsrfFromCookie();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const res = await fetch(`/api${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body,
    });
    if (!res.ok) {
      let detail: ApiError = { status: res.status, error: 'request_failed' };
      try {
        const json = (await res.json()) as Partial<ApiError>;
        detail = {
          status: res.status,
          error: typeof json.error === 'string' ? json.error : 'request_failed',
          issues: json.issues,
        };
      } catch {
        // ignore
      }
      throw new ApiResponseError(detail);
    }
    return (await res.json()) as T;
  },
};
